import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { supabase } from 'lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import ProfileNameField from './ProfileNameField';
import ProfileEmailField from './ProfileEmailField';
import ProfilePhoneField from './ProfilePhoneField';
import ProfileSchoolField from './ProfileSchoolField';

function validateName(name) {
  if (!name?.trim()) return 'Full name is required.';
  if (name?.trim()?.length < 2) return 'Name must be at least 2 characters.';
  if (name?.trim()?.length > 100) return 'Name must be 100 characters or fewer.';
  return '';
}

function validateEmail(email) {
  if (!email?.trim()) return 'Email address is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email?.trim())) return 'Please enter a valid email address.';
  return '';
}

function validateMobile(mobile) {
  if (!mobile?.trim()) return 'Mobile number is required.';
  if (mobile?.replace(/\D/g, '')?.length < 7) return 'Please enter a valid phone number.';
  return '';
}

function validateSchool(school, showSchool) {
  if (!showSchool) return '';
  if (!school?.trim()) return 'School or college is required.';
  return '';
}

export default function ProfileForm({ initialData, onSaveSuccess }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    mobile: initialData?.mobile || '',
    school: initialData?.school || '',
    dob: initialData?.dob || '',
  });

  const [errors, setErrors] = useState({});
  const [verifiedEmail, setVerifiedEmail] = useState(initialData?.email || '');
  const [verifiedPhone, setVerifiedPhone] = useState(initialData?.mobile || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Calculate age for school field conditional display
  const calculatedAge = React.useMemo(() => {
    if (!formData?.dob) return null;
    const today = new Date();
    const birth = new Date(formData?.dob);
    if (isNaN(birth?.getTime())) return null;
    let age = today?.getFullYear() - birth?.getFullYear();
    const monthDiff = today?.getMonth() - birth?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birth?.getDate())) {
      age -= 1;
    }
    return age;
  }, [formData?.dob]);

  const showSchoolField = calculatedAge === null || calculatedAge <= 24;

  // Detect if any field has changed from initial
  const hasChanges =
    formData?.name !== (initialData?.name || '') ||
    formData?.email !== (initialData?.email || '') ||
    formData?.mobile !== (initialData?.mobile || '') ||
    formData?.school !== (initialData?.school || '');

  // Check if changed fields requiring verification are verified
  const emailChanged = formData?.email !== (initialData?.email || '');
  const mobileChanged = formData?.mobile !== (initialData?.mobile || '');
  const emailOk = !emailChanged || verifiedEmail === formData?.email?.trim();
  const mobileOk = !mobileChanged || verifiedPhone === formData?.mobile;

  const canSave = hasChanges && emailOk && mobileOk;

  function handleChange(e) {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    setSaveError('');
  }

  function validate() {
    const newErrors = {};
    const nameErr = validateName(formData?.name);
    if (nameErr) newErrors.name = nameErr;
    const emailErr = validateEmail(formData?.email);
    if (emailErr) newErrors.email = emailErr;
    const mobileErr = validateMobile(formData?.mobile);
    if (mobileErr) newErrors.mobile = mobileErr;
    const schoolErr = validateSchool(formData?.school, showSchoolField);
    if (schoolErr) newErrors.school = schoolErr;
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  }

  async function handleSave(e) {
    e?.preventDefault();
    if (!validate()) return;
    if (!canSave) return;

    setIsSaving(true);
    setSaveError('');

    try {
      // Update Supabase auth metadata
      const { error: authError } = await supabase?.auth?.updateUser({
        email: formData?.email?.trim(),
        data: {
          full_name: formData?.name?.trim(),
          name: formData?.name?.trim(),
          mobile: formData?.mobile,
          school: formData?.school?.trim(),
        },
      });

      if (authError) {
        setSaveError(authError?.message || 'Failed to update profile. Please try again.');
        setIsSaving(false);
        return;
      }

      // Update volunteers table
      const { error: dbError } = await supabase
        ?.from('volunteers')
        ?.update({
          name: formData?.name?.trim(),
          email: formData?.email?.trim(),
          mobile: formData?.mobile,
          school: formData?.school?.trim(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('user_id', user?.id);

      if (dbError && dbError?.code !== 'PGRST116') {
        // PGRST116 = no rows found, not a fatal error
        console.warn('Volunteers table update warning:', dbError?.message);
      }

      setIsSaving(false);
      onSaveSuccess?.(formData);
    } catch (err) {
      setSaveError('Network error. Please try again.');
      setIsSaving(false);
    }
  }

  function handleCancelClick() {
    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      navigate('/volunteer-dashboard');
    }
  }

  function handleConfirmCancel() {
    setShowCancelConfirm(false);
    navigate('/volunteer-dashboard');
  }

  return (
    <>
      <form onSubmit={handleSave} noValidate className="space-y-6">
        {/* Name */}
        <ProfileNameField
          value={formData?.name}
          onChange={handleChange}
          error={errors?.name}
        />

        {/* Email */}
        <ProfileEmailField
          value={formData?.email}
          onChange={handleChange}
          error={errors?.email}
          onVerified={setVerifiedEmail}
          verifiedEmail={verifiedEmail}
          originalEmail={initialData?.email || ''}
        />

        {/* Mobile */}
        <ProfilePhoneField
          value={formData?.mobile}
          onChange={handleChange}
          error={errors?.mobile}
          onVerified={setVerifiedPhone}
          verifiedPhone={verifiedPhone}
          originalPhone={initialData?.mobile || ''}
        />

        {/* School — conditional on age */}
        {showSchoolField && (
          <ProfileSchoolField
            value={formData?.school}
            onChange={handleChange}
            error={errors?.school}
          />
        )}

        {/* Verification warning */}
        {hasChanges && (!emailOk || !mobileOk) && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700 flex items-start gap-2">
              <Icon name="AlertTriangle" size={15} className="mt-0.5 flex-shrink-0" />
              <span>
                {!emailOk && !mobileOk
                  ? 'Please verify your new email address and mobile number before saving.'
                  : !emailOk
                  ? 'Please verify your new email address before saving.'
                  : 'Please verify your new mobile number before saving.'}
              </span>
            </p>
          </div>
        )}

        {/* Save error */}
        {saveError && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md">
            <p className="text-sm text-destructive flex items-center gap-2">
              <Icon name="AlertCircle" size={14} />
              {saveError}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            type="submit"
            variant="default"
            size="lg"
            loading={isSaving}
            disabled={isSaving || !canSave}
            iconName="Save"
            iconPosition="left"
            iconSize={16}
            className="flex-1"
          >
            {isSaving ? 'Saving…' : 'Save Changes'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleCancelClick}
            disabled={isSaving}
            iconName="X"
            iconPosition="left"
            iconSize={16}
            className="flex-1 sm:flex-none"
          >
            Cancel
          </Button>
        </div>

        {!hasChanges && (
          <p className="text-xs text-muted-foreground text-center">
            Make changes to any field above to enable saving.
          </p>
        )}
      </form>

      {/* Cancel confirmation dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-card border border-border rounded-xl shadow-xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} color="var(--color-accent)" />
              </div>
              <h3 className="text-base font-heading font-semibold text-foreground">Discard changes?</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              You have unsaved changes. If you leave now, your changes will be lost.
            </p>
            <div className="flex gap-3">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleConfirmCancel}
                className="flex-1"
              >
                Discard
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1"
              >
                Keep Editing
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
